/**
 * API Client: BROWSE-V1 feature
 * SpecReferenceID: BROWSE-V1
 * Requirements: BROWSE-V1-R03, BROWSE-V1-R04
 *
 * Description: Typed client wrappers over the browse list + viewer
 *              endpoints. BrowsePage and ViewerPage consume these
 *              rather than calling `api.get` directly so every
 *              browse network access goes through a single
 *              inspectable seam (enforces the R11 local-store-only
 *              boundary on the frontend side).
 *
 * Risk Links: R9
 * FMEA Links: FM9
 */

import api from '@/lib/api';
import type { GuidelineDetail, GuidelineListItem } from './types';

/**
 * BROWSE-V1-R03: Fetch the sorted list of available guidelines from
 * the local browse store. Returns an empty array when the store is
 * empty (same shape the backend returns).
 */
export async function listGuidelines(): Promise<GuidelineListItem[]> {
  const response = await api.get<GuidelineListItem[]>('/browse/guidelines');
  return response.data;
}

/**
 * BROWSE-V1-R04: Fetch a single guideline by ID from the local
 * browse store. Returns the `BrowseGuidelineDetail` payload with
 * the verbatim `fullContent` blob the viewer renders.
 *
 * A missing or soft-deleted row surfaces as a 404 from the backend
 * (BROWSE-V1-R10); callers should inspect the axios-style error
 * response status and present a 404-specific UI before falling back
 * to a generic error message.
 */
export async function getGuideline(
  guidelineId: number,
): Promise<GuidelineDetail> {
  const response = await api.get<GuidelineDetail>(
    `/browse/guidelines/${guidelineId}`,
  );
  return response.data;
}
